import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, PackagePlus, Pencil, Printer, X } from 'lucide-react'
import { toast } from 'sonner'

import { reasonLabel } from './index'
import type { Search } from './index'
import { ProductLotStatusBadge } from '@/components/product-lots/ProductLotStatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useApproveProductLotInNote,
    useCancelProductLotInNote,
    useRejectProductLotInNote,
} from '@/hooks/use-product-lot-mutation'
import { useProductLotInNoteQuery } from '@/hooks/use-product-lot-query'
import { usePrintProductLotLabel } from '@/hooks/use-lot-label-print'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-in-notes/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data, isLoading } = useProductLotInNoteQuery(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const approve = useApproveProductLotInNote()
    const reject = useRejectProductLotInNote()
    const cancel = useCancelProductLotInNote()
    const printLot = usePrintProductLotLabel()
    const [rejectOpen, setRejectOpen] = useState(false)
    const [rejectReason, setRejectReason] = useState('')

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isPending = note.status === 'pending'
    const isCreator = !!user?.id && note.created_by_id === user.id
    const canApprove = hasPermission('product_lots.approve_in')
    const canWrite = hasPermission('product_lots.write')

    const handleApprove = async () => {
        if (!confirm(m.sl_detail_confirm_approve_in())) return
        try {
            await approve.mutateAsync(id)
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : m.sl_detail_error_approve(),
            )
        }
    }

    const handleReject = async () => {
        try {
            await reject.mutateAsync({ id, reason: rejectReason })
            setRejectOpen(false)
            setRejectReason('')
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_reject(),
            )
        }
    }

    const handleCancel = async () => {
        if (!confirm(m.sl_detail_confirm_cancel_in())) return
        try {
            await cancel.mutateAsync(id)
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_detail_error_cancel(),
            )
        }
    }

    return (
        <div className="space-y-4 p-4 md:p-6">
            <Button
                asChild
                variant="ghost"
                className="h-11 md:h-9 -ml-2 md:ml-0"
            >
                <Link
                    to="/sales-warehouse/stock-in-notes"
                    search={
                        recallListSearch<Search>(
                            LIST_SEARCH_KEYS.stockInNotes,
                        ) ?? {}
                    }
                >
                    <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                </Link>
            </Button>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PackagePlus className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <ProductLotStatusBadge status={note.status} />
                        </div>
                        {isPending && (
                            <div className="flex gap-2 flex-wrap">
                                {isCreator &&
                                    canWrite &&
                                    !note.stocktake_session_id && (
                                        <Button
                                            asChild
                                            variant="outline"
                                            className="h-11 md:h-9"
                                        >
                                            <Link
                                                to="/sales-warehouse/stock-in-notes/$id/edit"
                                                params={{ id }}
                                            >
                                                <Pencil className="size-4 mr-1" />
                                                {m.pl_pnk_edit_btn()}
                                            </Link>
                                        </Button>
                                    )}
                                {canApprove && (
                                    <>
                                        <Button
                                            className="h-11 md:h-9"
                                            onClick={handleApprove}
                                            disabled={approve.isPending}
                                        >
                                            <Check className="size-4 mr-1" />
                                            {m.sl_detail_action_approve()}
                                        </Button>
                                        <Button
                                            className="h-11 md:h-9"
                                            variant="destructive"
                                            onClick={() => setRejectOpen(true)}
                                        >
                                            <X className="size-4 mr-1" />
                                            {m.sl_detail_action_reject()}
                                        </Button>
                                    </>
                                )}
                                {isCreator && (
                                    <Button
                                        className="h-11 md:h-9"
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={cancel.isPending}
                                    >
                                        {m.sl_detail_action_cancel()}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <div className="col-span-2">
                        <Field
                            label={m.sl_detail_label_reason()}
                            value={reasonLabel(note.reason)}
                        />
                    </div>
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={
                                    note.status === 'approved'
                                        ? m.sl_detail_label_approver()
                                        : m.sl_detail_label_rejecter()
                                }
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                    {note.sales_order_id && (
                        <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">
                                {m.packaging_note_related_order()}
                            </p>
                            <Link
                                to="/orders/$orderId"
                                params={{ orderId: note.sales_order_id }}
                                className="text-base font-mono text-primary hover:underline md:text-sm"
                            >
                                {note.order_code || note.sales_order_id}
                            </Link>
                        </div>
                    )}
                    {note.stocktake_session_id && (
                        <div className="col-span-2">
                            <p className="text-xs text-muted-foreground">
                                {m.stocktake_related_note_link()}
                            </p>
                            <Link
                                to="/stocktake/$id"
                                params={{ id: note.stocktake_session_id }}
                                className="text-base font-mono text-primary hover:underline md:text-sm"
                            >
                                {note.stocktake_session_code ||
                                    note.stocktake_session_id}
                            </Link>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.pll_detail_col_lot_code()}</TableHead>
                            <TableHead>{m.pll_detail_col_product()}</TableHead>
                            <TableHead>{m.lot_position_label()}</TableHead>
                            <TableHead>{m.pll_detail_col_source()}</TableHead>
                            <TableHead>{m.pll_detail_col_materials()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_detail_col_in()} (kg)
                            </TableHead>
                            <TableHead className="text-right w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(note.items ?? []).map((it) => (
                            <TableRow key={it.id}>
                                <TableCell className="font-mono text-sm">
                                    <Link
                                        to="/sales-warehouse/lots/$id"
                                        params={{ id: it.product_lot_id }}
                                        className="text-primary hover:underline"
                                    >
                                        {it.product_lot_code ??
                                            it.product_lot_id}
                                    </Link>
                                </TableCell>
                                <TableCell>{it.product_name ?? '—'}</TableCell>
                                <TableCell className="font-mono text-sm">
                                    {it.position_code ?? '—'}
                                </TableCell>
                                <TableCell className="text-sm">
                                    {(it.sources ?? []).length > 0 ? (
                                        <div className="space-y-0.5">
                                            {(it.sources ?? []).map((s) => (
                                                <div
                                                    key={s.id}
                                                    className="font-mono"
                                                >
                                                    {s.source_scenting_lot_code ??
                                                        s.source_scenting_lot_id}
                                                    <span className="text-muted-foreground">
                                                        {' · '}
                                                        {formatQty(
                                                            s.source_quantity,
                                                        )}
                                                        kg
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                                <TableCell className="text-sm">
                                    {(it.materials ?? []).length > 0 ? (
                                        <div className="space-y-0.5">
                                            {(it.materials ?? []).map((mat) => (
                                                <div
                                                    key={mat.id}
                                                    className="font-mono"
                                                >
                                                    {mat.packaging_lot_code ??
                                                        mat.material_name ??
                                                        mat.packaging_lot_id}
                                                    <span className="text-muted-foreground">
                                                        {' · '}
                                                        {formatQty(mat.quantity)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatQty(it.quantity)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        aria-label={
                                            note.status !== 'approved'
                                                ? m.lot_label_print_btn_disabled_pending_approve()
                                                : m.lot_label_print_btn_title()
                                        }
                                        title={
                                            note.status !== 'approved'
                                                ? m.lot_label_print_btn_disabled_pending_approve()
                                                : m.lot_label_print_btn_title()
                                        }
                                        disabled={
                                            printLot.isPending ||
                                            note.status !== 'approved'
                                        }
                                        onClick={() =>
                                            printLot.mutate(it.product_lot_id)
                                        }
                                    >
                                        <Printer className="size-4" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.sl_detail_dialog_reject_title_in()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>{m.sl_detail_dialog_reject_label()}</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder={m.sl_detail_dialog_reject_placeholder()}
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={() => setRejectOpen(false)}
                        >
                            {m.sl_detail_dialog_cancel_btn()}
                        </Button>
                        <Button
                            variant="destructive"
                            className="h-11 flex-1 sm:h-10 sm:flex-none"
                            onClick={handleReject}
                            disabled={reject.isPending}
                        >
                            {m.sl_detail_dialog_confirm_reject_btn()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
